import { getAccessToken } from './auth.ts';
import { accessChanged } from './session.ts';
import { runtimeConfig } from './runtime.ts';
import { t } from './copy.ts';

/**
 * Every call goes through here so the access token is attached in one place and
 * the contract's error shape is unwrapped in one place.
 */
export interface ApiError {
  code: 'unauthenticated' | 'forbidden' | 'not_found' | 'conflict' | 'validation_failed' | 'unavailable';
  message: string;
  fields?: { path: string; problem: string }[];
}

export class ApiFailure extends Error {
  readonly code: ApiError['code'];
  readonly fields: ApiError['fields'];
  readonly status: number;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiFailure';
    this.status = status;
    this.code = error.code;
    this.fields = error.fields;
  }
}

const BASE = () => runtimeConfig().apiBaseUrl;

/**
 * Nothing waits forever.
 *
 * `fetch` has no timeout of its own: a request that is never answered — a
 * phone that has wandered off the network, a connection a carrier has quietly
 * dropped — leaves a promise pending for as long as the tab is open. Every
 * await of it then hangs too, and a page that says "Signing you in..." goes on
 * saying it until somebody closes the tab. That is what two beta testers hit on
 * their phones.
 *
 * Twenty seconds is long enough for a slow connection and short enough that
 * somebody still believes the screen is talking to them.
 */
const TIMEOUT_MS = 20_000;

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  let res: Response;
  try {
    res = await fetch(`${BASE()}${path}`, {
      ...init,
      signal: init.signal ?? AbortSignal.timeout(TIMEOUT_MS),
      headers: {
        ...(init.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
  } catch (err) {
    // A timeout and a dropped connection are the same thing to whoever is
    // waiting: it did not get through, and trying again is the answer.
    const timedOut = err instanceof DOMException
      && (err.name === 'TimeoutError' || err.name === 'AbortError');
    throw new ApiFailure(0, {
      code: 'unavailable',
      message: timedOut ? t('error.timeout') : t('error.offline'),
    });
  }

  if (res.status === 204) return undefined as T;

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const error = (body as { error?: ApiError } | null)?.error;
    /*
     * When there is no JSON error to unwrap — a proxy's own 502, say — report
     * the HTTP status. "502 Bad Gateway" sends somebody to the right place;
     * the parse failure that produced it sends them into the JSON parser.
     */
    /*
     * A refusal naming a scope means the token is out of date rather than the
     * account being wrong: scopes are fixed when a token is minted, and a
     * role granted afterwards is not in it. The shell offers a way back; see
     * `accessChanged`.
     */
    if (res.status === 403 && /requires scope/i.test(error?.message ?? '')) {
      accessChanged.value = true;
    }

    throw new ApiFailure(res.status, error ?? {
      code: res.status >= 500 ? 'unavailable' : 'validation_failed',
      message: `Something went wrong (${res.status} ${res.statusText || 'error'}). `
        + 'Please try again.',
    });
  }
  return body as T;
}

/**
 * Errors say what happened and what to do. The API's message is written for a
 * person, so it is shown as-is; these are the fallbacks for the cases where
 * only a code arrives.
 */
export function humanMessage(err: unknown): string {
  if (!(err instanceof ApiFailure)) return t('error.generic');
  // The API writes its messages for a person, so they are shown as-is. These
  // are the fallbacks for when only a code arrives.
  if (err.message) return err.message;
  switch (err.code) {
    case 'unauthenticated':   return t('error.unauthenticated');
    case 'forbidden':         return t('error.forbidden');
    case 'not_found':         return t('error.notFound');
    case 'conflict':          return t('error.conflict');
    case 'validation_failed': return t('error.validationFailed');
    case 'unavailable':       return t('error.unavailable');
  }
}
